// Spec §4.3: Extract debounce click logic from IconButton component
import dayjs from 'dayjs';
import { useRef } from 'react';
import type { IconButtonProps } from './types';

/**
 * Stand-in click handler used when the icon button receives no onClick prop. Performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Supplies the icon button with a click handler that guards against double submits. Defaults the onClick prop to a no-op and, unless debouncing is disabled, drops clicks arriving within 300ms of the previous one recorded in a ref.
 *
 * @param props - Button props carrying onClick and isDebounceClick.
 * @returns The debounced click handler.
 */
export const useIconButton = (props: IconButtonProps) => {
  const { isDebounceClick = true, onClick: onClickProp = NOOP } = props;
  const debounceClickBottomButton = useRef(0);

  /**
   * Debounced click handler for the icon button. Returns early when debouncing is enabled and the previous click was less than 300ms ago, otherwise records the current timestamp and invokes the wrapped onClick prop.
   *
   * @param e - The click event forwarded to the wrapped handler.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    if (isDebounceClick && dayjs().valueOf() - debounceClickBottomButton.current < 300) {
      return;
    }

    debounceClickBottomButton.current = dayjs().valueOf();
    onClickProp(e);
  };

  return { onClick };
};
